import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { API_URL } from '../utils/api';
import type { Customer } from '../contexts/CustomerAuthContext';
import type { PassportData, RestaurantProgress } from '../types/passport';

const CONFETTI_STAMP_COUNT_KEY = 'olso_passport_stamp_count';

interface UsePassportDataOptions {
  effectiveToken: string | null;
  effectiveCustomer: Customer | null;
  justLoggedIn?: { customer: Customer; token: string } | null;
  onLogout: () => void;
  onShowAuth?: () => void;
}

export function usePassportData(options: UsePassportDataOptions) {
  const { effectiveToken, effectiveCustomer, justLoggedIn, onLogout, onShowAuth } = options;

  const [data, setData] = useState<PassportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth state
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);

  // Card flip
  const [isFlipped, setIsFlipped] = useState(false);

  // Achievement reveal chain
  const [revealAchIds, setRevealAchIds] = useState<string[]>([]);
  const [showAchOverlayId, setShowAchOverlayId] = useState<string | null>(null);
  const [flyOutAchIds, setFlyOutAchIds] = useState<string[]>([]);
  const [stampedAchIds, setStampedAchIds] = useState<string[]>([]);

  const prevLoyaltyPointsRef = useRef<number>(0);

  // Count-up display values
  const [displayOrders, setDisplayOrders] = useState(0);
  const [displayPoints, setDisplayPoints] = useState(0);
  const [displayRestaurants, setDisplayRestaurants] = useState(0);

  // Stamp selection
  const [selectedStamp, setSelectedStamp] = useState<RestaurantProgress | null>(null);
  const [selectedStampColor, setSelectedStampColor] = useState<string>('#FF5A00');

  // Badge detail
  const [selectedBadge, setSelectedBadge] = useState<{
    id: string;
    restaurantId: string;
    badgeName: string;
  } | null>(null);

  const [badgeStats, setBadgeStats] = useState<{
    visitCount: number;
    currentTier: { name: string; threshold: number; imageUrl: string | null } | null;
    nextTier: { name: string; threshold: number; imageUrl: string | null } | null;
    favoriteDish: { id: string; name: string; imageUrl: string | null; timesOrdered: number } | null;
  } | null>(null);

  const [badgeStatsLoading, setBadgeStatsLoading] = useState(false);

  // Show username prompt for new users (no username set)
  useEffect(() => {
    if (effectiveCustomer && !effectiveCustomer.username && justLoggedIn) {
      // Small delay so the passport panel renders first
      const timer = setTimeout(() => setShowUsernamePrompt(true), 600);
      return () => clearTimeout(timer);
    }
  }, [effectiveCustomer, justLoggedIn]);

  // Passport data fetch
  useEffect(() => {
    if (!effectiveToken || !effectiveCustomer) {
      setLoading(false);
      return;
    }
    const base = API_URL || '';
    axios
      .get(`${base}/api/auth/customer/me`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      })
      .then((res) => {
        setData(res.data);
        setError('');
      })
      .catch((e) => {
        const msg = e.response?.data?.error || 'Failed to load';
        setError(msg);
        if (e.response?.status === 401) {
          onLogout();
          onShowAuth?.();
        }
      })
      .finally(() => setLoading(false));
  }, [effectiveToken, effectiveCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confetti burst only when stamp count grows (new restaurant visited)
  useEffect(() => {
    if (!data || data.restaurants.length === 0) return;
    const seen = parseInt(localStorage.getItem(CONFETTI_STAMP_COUNT_KEY) || '0', 10);
    if (data.restaurants.length > seen) {
      confetti({
        particleCount: 50,
        spread: 65,
        origin: { y: 0.6 },
        colors: ['#FF5A00', '#FF8C42', '#FCD34D', '#fff'],
        zIndex: 9999,
      });
      localStorage.setItem(CONFETTI_STAMP_COUNT_KEY, String(data.restaurants.length));
    }
  }, [data?.restaurants?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect newly unlocked achievements and start reveal chain.
  // Source of truth: notifiedAt == null in DB means "not yet shown".
  // This is device/browser/incognito-independent.
  useEffect(() => {
    if (!data) return;

    const newIds = data.achievements
      .filter(a => a.notifiedAt === null)
      .map(a => a.id);

    if (newIds.length === 0) return;

    setRevealAchIds(newIds);

    // Step 2: show overlay after 1000ms
    const t1 = setTimeout(() => {
      setShowAchOverlayId(newIds[0]);
    }, 1000);

    return () => clearTimeout(t1);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count-up animation for stats
  useEffect(() => {
    if (!data) return;
    const targets: [number, Dispatch<SetStateAction<number>>][] = [
      [data.customer.totalOrders, setDisplayOrders],
      [data.customer.loyaltyPoints, setDisplayPoints],
      [data.restaurants.length, setDisplayRestaurants],
    ];
    targets.forEach(([target, setter]) => {
      const duration = 800;
      const start = performance.now();
      const animate = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        setter(Math.round(progress * target));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverlayDone = () => {
    const currentId = showAchOverlayId;
    setShowAchOverlayId(null);

    // Mark this achievement as seen — optimistically update local state so rapid
    // re-opens don't re-trigger the overlay before the API call completes.
    if (currentId) {
      setData(prev => prev ? {
        ...prev,
        achievements: prev.achievements.map(a =>
          a.id === currentId ? { ...a, notifiedAt: new Date().toISOString() } : a
        ),
      } : prev);
    }
    if (currentId && effectiveCustomer && effectiveToken) {
      axios.post(
        `${API_URL || ''}/api/achievements/customer/${effectiveCustomer.id}/mark-seen`,
        { achievementIds: [currentId] },
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      ).catch(() => { /* non-critical — local state already updated above */ });
    }

    // Step 3: row flies out 150ms after overlay closes
    setTimeout(() => {
      if (currentId) setFlyOutAchIds(prev => [...prev, currentId]);
    }, 150);

    // Step 4: stamp into collection 300ms after overlay closes (150ms after row exit starts)
    setTimeout(() => {
      if (currentId) setStampedAchIds(prev => [...prev, currentId]);
    }, 300);

    // Step 6: points tick — animate displayPoints from prev to current
    const prev = prevLoyaltyPointsRef.current;
    const target = data?.customer.loyaltyPoints ?? 0;
    if (target > prev && prev > 0) {
      setTimeout(() => {
        const duration = 700;
        const start = performance.now();
        const animate = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          setDisplayPoints(Math.round(prev + (target - prev) * ease));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }, 1000);
    }
    prevLoyaltyPointsRef.current = target;
  };

  const fetchBadgeStats = async (cId: string, restaurantId: string) => {
    setBadgeStatsLoading(true);
    try {
      const { data: statsData } = await axios.get(
        `${API_URL || ''}/api/badges/customer/${cId}/restaurant/${restaurantId}/stats`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      setBadgeStats(statsData);
    } catch {
      setBadgeStats(null);
    } finally {
      setBadgeStatsLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    // Auth state
    showUsernamePrompt,
    setShowUsernamePrompt,
    // Card flip
    isFlipped,
    setIsFlipped,
    // Achievement reveal chain
    revealAchIds,
    showAchOverlayId,
    flyOutAchIds,
    stampedAchIds,
    handleOverlayDone,
    // Count-up display values
    displayOrders,
    displayPoints,
    displayRestaurants,
    // Stamp selection
    selectedStamp,
    setSelectedStamp,
    selectedStampColor,
    setSelectedStampColor,
    // Badge detail
    selectedBadge,
    setSelectedBadge,
    badgeStats,
    badgeStatsLoading,
    fetchBadgeStats,
  };
}
